/* eslint-disable class-methods-use-this */
import get from 'lodash/get';
import each from 'lodash/each';
import pick from 'lodash/pick';
import extend from 'lodash/extend';
import find from 'lodash/find';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import PageData from '@Services/PageData';
import AppStorage from '@Services/AppStorage';
import AccountData from '@Services/AccountData';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import { getCombinedRouteParams, getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import CatalystConfig from './CatalystConfig';
import { ErrorMessageLimitMap } from './CatalystConstants';

const contextMap = {
  citations: 'citations',
  reactions: 'reactions',
  substances: 'substances',
  tgitems: 'targets',
  dpitems: 'heatmap',
};

export class CatalystService {
  search = {};

  _cleanUpSearch = false;

  constructor() {
    // AA initial page load time
    window.addEventListener('load', () => {
      let loadTime = '';
      try {
        const domComplete = performance.getEntriesByType?.('navigation')?.[0]?.domComplete;
        loadTime = domComplete ? Math.round(domComplete) : '';
        // eslint-disable-next-line sonarjs/no-ignored-exceptions,no-unused-vars
      } catch (e) {
        // loadTime remains ''
      }
      PageData.setInitialPageLoad(loadTime);
    });
  }

  waitForAdobeService = (url) =>
    new Promise((resolve) => {
      const attemptAccess = (attempts = 0) => {
        if (window.s && window.s.visitor) {
          resolve(window.s.visitor.appendVisitorIDsTo(url));
        } else if (attempts < 20) {
          setTimeout(() => attemptAccess(attempts + 1), 250);
        } else {
          resolve(url);
        }
      };
      attemptAccess();
    });

  appendVisitorIDToUrl = (url) =>
    this.waitForAdobeService(url).catch(() => {
      Logger.error('window.s.visitor did not load after waiting 5 seconds.');
      return url;
    });

  /**
   * @name parseError
   * @methodOf trackError
   * @param {object} error Error object to be parsed for result message
   * @return {Array} Array with truncated messages
   */
  _parseError = (error) => {
    const errorMessages = [];

    Object.keys(ErrorMessageLimitMap).forEach((key) => {
      let message = error[key];

      if (message) {
        message = message.toString().substring(0, ErrorMessageLimitMap[key]);
      }

      errorMessages.push(message);
    });

    return errorMessages;
  };

  /**
   * @name generateTrackedObject
   * @param {String} route
   * @param {Object} params
   * @returns {Object} tracked object
   */
  _generateTrackedObject = (route, params) => ({
    page: PageData.getPageData(route, params),
    visitor: AccountData.getVisitorData(),
  });

  /**
   * @name _checkLoginSuccess
   * @param {Object} trackedObject
   * @description reset visitor.loginSuccess to "false" if it was "true", so event23 fires only once
   * Also updates trackedObject if anonymous
   */
  _checkLoginSuccess = (trackedObject) => {
    if (get(trackedObject, 'visitor.loginSuccess') === 'true') {
      // reset 'false' for next events
      AccountData.updateExtendedData({ loginSuccess: 'false' });
      // if anonymous - set to false for current trackedObject
      if (AuthHelper.isAnonymousUser()) {
        extend(trackedObject.visitor, { loginSuccess: 'false' });
      }
    }
  };

  // AA async loaded uses window.appData
  _trackEvent = (eventName, eventData) => {
    if (!window.appData) {
      window.appData = [];
    }
    // Push is important, window.appData overrides it to make AA calls
    window.appData.push({
      event: eventName,
      ...eventData,
    });
  };

  /**
   * @name _trackPageViewEvent
   * @param {String} eventName
   * @param {Object} trackedObject
   * @param {String} route
   */
  _trackPageViewEvent = (eventName, trackedObject, route) => {
    if (!get(trackedObject, 'page.name')) {
      Logger.warn(`Route ${route} is not defined for tracking in catalyst service`);
      return;
    }

    // check and update loginSuccess
    this._checkLoginSuccess(trackedObject);

    if (PageData.shouldTrackPageLoad()) {
      this._trackEvent('pageLoad', trackedObject);
      PageData.unsetInitialPageLoad();
    } else {
      this._trackEvent(eventName, trackedObject);
    }
  };

  /**
   * @name extractFromBio
   * @param {Array} commonData
   * @description count documents and datapoints for bio tables
   */
  _extractFromBio = (commonData) => {
    const count = { documents: 0, datapoints: 0 };

    count.datapoints = commonData ? commonData.length : 0;

    each(commonData, (row) => {
      count.documents += get(row, 'details.citations', []).length;
    });

    return count;
  };

  /**
   * @name extractFromRows
   * @param {Array} commonData
   * @description count documents and datapoints for quan tables
   */
  _extractFromRows = (commonData) => {
    const count = { documents: 0, datapoints: 0 };

    count.datapoints = commonData ? commonData.length : 0;

    each(commonData, (row) => {
      const citations = find(row.cells, { id: 'citation' });
      if (citations && citations.value) {
        count.documents += citations.value.length;
      }
    });

    return count;
  };

  /**
   * @ngdoc method
   * @name getPrefix
   * @methodOf  Catalyst.CatalystService
   * @description Get prefix from CatalystConfig
   */
  getPrefix = () => CatalystConfig.getPrefix();

  trackEvent = (eventName, param) => {
    this._trackEvent(eventName, param);
  };

  mapContext = (context) => contextMap[context];

  /**
   * @name trackLoginStart
   * @methodOf  Catalyst.CatalystService
   * @param  {string} event name
   * @description tracks simple event without any data
   */
  trackSimpleEvent = (event) => {
    this._trackEvent(event);
  };

  /**
   * @name trackPage
   * @methodOf  Catalyst.CatalystService
   * @param {String} route
   * @param {Object} params
   * @param {Object} config
   */
  // eslint-disable-next-line consistent-return
  trackPage = (route, params, config) => {
    if ((config && config.trackAACustom) || get(params, 'trackAACustom') === 'force') {
      return null;
    }

    PageData.setPageTimestamp(route, params);
    const trackedObject = this._generateTrackedObject(route, params);
    extend(window.pageData, trackedObject);

    this._trackPageViewEvent(CatalystConfig.getNewPageEventName(), trackedObject, route);
  };

  /**
   * @name setSearchResults
   * @methodOf  Catalyst.CatalystService
   * @param {Object} search
   * @param {Boolean} update
   * @description saves search object either to the search instance property (for next searchResultsUpdated)
   * or pageData.search (for next page view event)
   */
  setSearchResults = (search, update = false) => {
    if (update) {
      extend(this.search, search);
      this._cleanUpSearch = true;
      return;
    }
    extend(window.pageData, {
      search,
    });
  };

  /**
   * @name updateSearchObject
   * @methodOf  Catalyst.CatalystService
   * @param {Object} searchObj
   * @description Update search object directly in window.pageData.search. I used when need to reset some values,
   * since "searchResultsUpdated" doesn't set null, undefined or other empty values
   */
  updateSearchObject = (searchObj) => {
    if (window.pageData) {
      window.pageData.search = window.pageData.search || {};
      extend(window.pageData.search, searchObj);
    }
  };

  /**
   * @name trackSearchResults
   * @methodOf  Catalyst.CatalystService
   * @param {String} eventName
   * @param {Object} params
   */
  trackSearchResults = (eventName, params, state) => {
    const stateCurrentName = getTrackingPathName(state) || '';
    PageData.setPageTimestamp(stateCurrentName, params);
    // eslint-disable-next-line no-param-reassign
    params = extend(params, this.search);

    const customTrackObject = { search: params };
    const route = stateCurrentName;
    const trackedObject = this._generateTrackedObject(route, params);
    extend(window.pageData, trackedObject);

    this._trackPageViewEvent(eventName, extend(customTrackObject, trackedObject), route);

    if (this._cleanUpSearch) {
      this.search = {};
      this._cleanUpSearch = false;
    }
  };

  /**
   * @ngdoc method
   * @name trackContentView
   * @methodOf  Catalyst.CatalystService
   * @param {String} eventName
   * @param {Object} content
   * @param {Object} extended
   */
  trackContentView = (eventName, content, extended) => {
    this._trackEvent(eventName, { content: [content], ...extended });
  };

  /**
   * @name trackExport
   * @methodOf  Catalyst.CatalystService
   * @param {Object} content
   * @param {Number} rows
   * @param {Array} selectedDatapoints
   */
  trackExport = (content, rows, selectedDatapoints) => {
    if (!content) {
      this._trackEvent('contentExportStart');
      return;
    }

    const trackingObj = {
      content: [content],
    };

    if (!isUndefined(rows)) {
      extend(trackingObj, {
        export: {
          rows,
        },
      });
    }

    // RXFE-7886: AA - Track the data points selected during export
    if (!isEmpty(selectedDatapoints)) {
      // eslint-disable-next-line no-param-reassign
      content.exportType = `${content.exportType}&${map(selectedDatapoints, 'name').join('|')}`;
    }

    this._trackEvent('rowsExported', trackingObj);
  };

  trackExportDownload = (content) => {
    this._trackEvent('contentDownload', { content: [content] });
  };

  /**
   * @name trackLinkOut
   * @methodOf  Catalyst.CatalystService
   * @param {Object} content
   * @param {String} link
   */
  trackLinkOut = (content, link) => {
    const trackingObj = {};

    if (link) {
      extend(trackingObj, { link });
    }

    if (content) {
      extend(trackingObj, { content: [content] });
    }

    if (!isEmpty(trackingObj)) {
      this._trackEvent('linkOut', trackingObj);
    }
  };

  /**
   * @name trackError
   * @methodOf Catalyst.CatalystService
   * @param {object} error Error object to be parsed for result message
   * @param {string} prefix
   */
  trackError = (error, prefix = 'ae-') => {
    if (!error) {
      return;
    }
    const accessToken = AuthHelper.getAccessToken();
    let errorType = prefix + this._parseError(error).join('|');
    if (accessToken) {
      errorType = errorType.replace(accessToken, 'token');
    }

    this._trackEvent('formError', { form: { errorType } });
  };

  /**
   * @name trackContentLinkClick
   * @methodOf  Catalyst.CatalystService
   * @param {string} name String containing properties names concatenated with
   * ':' from parent to children (e.g. 'parent:child:another child')
   * @param {string} [type]
   * @param {string} [actionName]
   * @param {string} [location]
   * @description
   * Tracks when certain product and content features are triggered on UI
   */
  // eslint-disable-next-line default-param-last
  trackContentLinkClick = (name, type = 'in-page', actionName, location) => {
    const trackObj = {
      link: {
        name,
        type,
        ...(location && { location }),
      },
    };

    if (actionName) {
      extend(trackObj, {
        action: {
          name: actionName,
        },
      });
    }

    this._trackEvent('contentLinkClick', trackObj);
  };

  /**
   * @name trackContextLinkClick
   * @methodOf  Catalyst.CatalystService
   * @param {string} name of clicked context
   * @description tracks when context link on substance page was clicked
   */
  trackContextLinkClick = (name) => {
    this._trackEvent('contentLinkClick', {
      link: {
        name,
        type: 'exit',
      },
      eventName: 'contentLinkClick',
      action: {
        name: `rx2:exit:substances:${name}`,
      },
    });
  };

  /**
   * @name trackContentInteraction
   * @methodOf  Catalyst.CatalystService
   * @param {string} name String containing properties names concatenated with ':'
   * @param {array} content - additional tracking content if needed
   * @description currently tracks structure buttons clicks
   */
  trackContentInteraction = (name, content) => {
    this._trackEvent('contentInteraction', {
      action: {
        name,
      },
      content,
    });
  };

  /**
   * @name refreshVisitorData
   * @methodOf  Catalyst.CatalystService
   * @param {Boolean} force
   */
  refreshVisitorData = (force) => {
    if (!window.pageData) {
      return;
    }

    const visitorData = {
      visitor: AccountData.getVisitorData(),
    };

    if (force) {
      // missed props from visitorData will be removed from window.pageData
      extend(window.pageData, visitorData);
      return;
    }
    // missed props from visitorData will be kept in window.pageData
    merge(window.pageData, visitorData);
  };

  /**
   * @name trackProductFeature
   * @methodOf  Catalyst.CatalystService
   * @param {String} featureName
   */
  trackProductFeature = (featureName) => {
    this._trackEvent('productFeatureUsed', {
      feature: {
        name: featureName,
      },
    });
  };

  /**
   * @name trackSearchResultsClick
   * @methodOf  Catalyst.CatalystService
   * @param {String} buttonPosition
   * @param {Object} extended
   */
  trackSearchResultsClick = (buttonPosition, extended) => {
    this._trackEvent('searchResultsClick', {
      search: {
        resultsPosition: buttonPosition,
      },
      ...extended,
    });
  };

  /**
   * @name trackIntegratedFeedbackRendered
   * @methodOf  Catalyst.CatalystService
   */
  trackIntegratedFeedbackRendered = () => {
    this._trackEvent('ctaImpression', {
      cta: {
        ids: ['rx2:ift:step1'],
      },
    });
  };

  /**
   * @name trackIntegratedFeedbackClosed
   * @methodOf  Catalyst.CatalystService
   */
  trackIntegratedFeedbackClosed = () => {
    this._trackEvent('ctaClick', {
      cta: {
        ids: ['rx2:ift:step1:close'],
      },
    });
  };

  /**
   * @name trackIntegratedFeedbackStep2
   * @methodOf  Catalyst.CatalystService
   */
  trackIntegratedFeedbackStep2 = () => {
    this._trackEvent('ctaImpression', {
      cta: {
        ids: ['rx2:ift:step2'],
      },
    });
  };

  /**
   * @name trackIntegratedSearchClick
   * @methodOf  Catalyst.CatalystService
   * @param {number} ratingLevel
   */
  trackIntegratedSearchClick = (ratingLevel) => {
    this._trackEvent('SurveySubmission', {
      survey: {
        type: 'SEARCH',
        score: ratingLevel,
        question: 'How satisfied are you with the result set?',
      },
    });
  };

  /**
   * @name trackIntegratedSearchFeedbackClick
   * @methodOf  Catalyst.CatalystService
   * @param {number} ratingLevel
   * @param {String} feedback
   * @param selectedFeedback
   */
  trackIntegratedSearchFeedbackClick = (ratingLevel, feedback, selectedFeedback) => {
    const feedbackSuffix = feedback ? ` : ${feedback}` : '';
    this._trackEvent('SurveySubmission', {
      survey: {
        type: 'SEARCH',
        score: ratingLevel,
        comment: `${selectedFeedback}${feedbackSuffix}`,
        question: 'I chose this rating because the results:',
      },
    });
  };

  /**
   * @name setNoTracking
   * @methodOf  Catalyst.CatalystService
   * @param {Boolean | 'true' | any} noTracking
   */
  // cover with tests
  setNoTracking = (noTracking) => {
    const isTrackObject = {
      page: {
        noTracking: !isUndefined(noTracking) ? noTracking.toString().toLowerCase() : 'false',
      },
    };
    CatalystConfig.setNoTracking(isTrackObject.page.noTracking);
    merge(window.pageData, isTrackObject);
  };

  /**
   * @name trackRegistrationStarted
   * @methodOf Catalyst.CatalystService
   * @description generates event 1, when the user is presented with the registration screen
   */
  trackRegistrationStarted = () => {
    this._trackEvent('formView', {
      form: {
        step: 'start',
        type: 'registration',
      },
    });
  };

  /**
   * @name trackRegistrationCompleted
   * @methodOf Catalyst.CatalystService
   * @description generates event 2 on click after successful registration
   */
  trackRegistrationCompleted = () => {
    this._trackEvent('formView', {
      form: {
        step: 'complete',
        type: 'registration',
      },
    });
  };

  /**
   * @name trackSavedAlert
   * @methodOf Catalyst.CatalystService
   * @param {Object} alert
   * @description generates event on alert save
   */
  trackSavedAlert = (alert) => {
    this._trackEvent('saveAlert', {
      alert: {
        frequency: alert.frequency,
        type: alert.trackingType || `alert.${alert.context}`,
        ...(alert.details && { details: alert.details }),
      },
      search: {
        criteria: alert.description,
      },
    });
  };

  /**
   * @name trackSaveSearch
   * @methodOf  Catalyst.CatalystService
   * @param {Object} search
   */
  trackSaveSearch = (search) => {
    if (search) {
      this._trackEvent('saveSearch', {
        search,
      });
    }
  };

  /**
   * @name trackAmountOfViewedData
   * @methodOf  Catalyst.CatalystService
   * @param {Object} id
   * @param {Object} data
   * @param {String} mode
   * @param {String} title
   * @param {Object} state
   * @param {Number} firstItem
   * @param {Number} lastItem
   * @description track amount of viewed data
   */
  trackAmountOfViewedData = (id, data, mode, title, state, firstItem, lastItem) => {
    const combinedParams = getCombinedRouteParams(state);
    const resultName = data.resultName ? `:${data.resultName}` : '';
    const isShowMore = mode === 'showMoreBioqual' || mode === 'showMoreBioquan' || mode === 'showMoreQuan';
    const viewedData = AppStorage.instance.getFromSessionStorage('viewedDataAA') || {};

    let viewedDataId;

    if (data.heatmapContentView) {
      const showMoreSuffix = isShowMore ? `:${mode}` : '';
      viewedDataId = `${combinedParams.searchId}:${data.heatmapContentView.xName}:${data.heatmapContentView.yName}${showMoreSuffix}`;
    } else {
      const showMoreSuffix = isShowMore ? `:${mode}` : '';
      viewedDataId = `${id}:${title}${resultName}${showMoreSuffix}`;
    }

    // need to remember what info in table was shown after using showMore/showAll
    if (firstItem && lastItem) {
      viewedDataId += `(${firstItem}-${lastItem})`;
    }

    // avoid multiple click tracking on the same element
    if (viewedData[viewedDataId]) {
      return;
    }

    viewedData[viewedDataId] = true;
    AppStorage.instance.setToSessionStorage('viewedDataAA', viewedData);

    const { documents, datapoints } = this.getDocsAndDatapointsCount(data, mode);

    // avoid tracking for empty documents and datapoints
    if (!documents && !datapoints) {
      return;
    }

    const trackedType = this.getTrackedType(data, title, state);
    const type = get(data, 'extendedTitle', trackedType);

    this._trackEvent('contentView', {
      content: [
        {
          id,
          type,
          datapoints,
          documents,
        },
      ],
    });
  };

  trackHeatmapContentView = (documents, state) => {
    const combinedParams = getCombinedRouteParams(state);
    const type = 'rx2:results:heatmap';
    const viewedDataId = `${type}:${combinedParams.searchId}`;
    const viewedData = AppStorage.instance.getFromSessionStorage('viewedDataAA') || {};

    if (viewedData[viewedDataId]) {
      return;
    }

    viewedData[viewedDataId] = true;
    AppStorage.instance.setToSessionStorage('viewedDataAA', viewedData);

    this.trackContentView('contentView', {
      id: 'heatmap',
      type,
      documents,
    });
  };

  /**
   * @name getTrackedType
   * @param {Object} data
   * @param {String} title
   * @param {Object} state
   * @description prepares type of item for AA tracking (expand/collapse actions)
   */
  getTrackedType = (data, title, state) => {
    const pageNames = {
      'main.search.quick.results': 'rx2:query:preview',
      'main.results.context.substances': 'rx2:results:substances',
      'main.results.context.reactions': 'rx2:results:reactions',
      'main.results.heatmap.dpitems': 'rx2:results:heatmap',
    };

    const context = state.params.context ? `.${state.params.context}` : '';

    const page = pageNames[state.current.name + context];
    return `${page}:${title}`;
  };

  /**
   * @name getDocumentsCount
   * @param {Object | Array} data
   * @param {String} mode
   * @description count documents
   */
  getDocsAndDatapointsCount = (data, mode) => {
    let quanData;
    let qualData;
    let commonData;
    switch (mode) {
      case 'bio':
      case 'category':
        quanData = get(data, 'bioData.quan.data', []);
        qualData = get(data, 'bioData.qual.data', []);
        commonData = quanData.concat(qualData);
        return this._extractFromBio(commonData);

      case 'showMoreBioqual':
        return this._extractFromBio(data);

      case 'showMoreBioquan':
        return this._extractFromBio(data);

      case 'field':
        quanData = get(data, 'quanData.rows', []);
        qualData = get(data, 'qualData.rows', []);
        commonData = quanData.concat(qualData);

        return this._extractFromRows(commonData);

      case 'showMoreQuan':
        return this._extractFromRows(data);

      default:
        return { documents: 0, datapoints: 0 };
    }
  };

  /**
   * @name resetAutosuggestEvars
   * @methodOf  Catalyst.CatalystService
   * @description resets autosuggest-related pageData.search fields
   */
  resetAutosuggest = () => {
    const autosuggestProps = [
      'typedTerm',
      'selectedTerm',
      'suggestedClickPosition',
      'suggestedLetterCount',
      'suggestedResultCount',
      'autoSuggestCategory',
      'autoSuggestDetails',
    ];
    const searchObj = get(window, 'pageData.search');
    const pickRes = pick(searchObj, autosuggestProps);
    const res = mapValues(pickRes, () => null);
    extend(searchObj, res);
  };

  /**
   * @name trackUserProfileUpdate
   * @methodOf  Catalyst.CatalystService
   * @param {String} fieldsUpdated
   * @description tracks user profile changes
   */
  trackUserProfileUpdate = (fieldsUpdated) => {
    this._trackEvent('userProfileUpdate', {
      user: { fieldsUpdated },
    });
  };

  /**
   * @name trackConversionDriverClick
   * @methodOf  Catalyst.CatalystService
   * @param {String} action
   * @description tracks usage of login/register from different places
   */
  trackConversionDriverClick = (action) => {
    this._trackEvent('conversionDriverClick', {
      conversionDriver: {
        name: `${this.getPrefix()}:kmau:${action}`,
      },
    });
  };
}

const Catalyst = new CatalystService();
export default Catalyst;
